import { useCallback, useEffect, useState } from 'react';
import { Sparkles, Shirt, Sword, Heart } from 'lucide-react';
import { listRecentPublicAssets } from '../lib/db/assets';
import { formatCurrency } from '../lib/formatters';
import Mini3DViewer from './Mini3DViewer';
import type { GeneratedAsset } from '../lib/db/types';
import { useNavigate } from 'react-router-dom';
import {
  filterAssetsWithValidPublicPreviews,
  type ValidatedFrontPageAsset,
} from '../utils/frontPagePreviewValidation';

export default function CommunityGallerySection() {
  const DISPLAY_LIMIT = 6;
  const CANDIDATE_FETCH_LIMIT = 18;
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Array<ValidatedFrontPageAsset<GeneratedAsset>>>([]);
  const [loading, setLoading] = useState(true);

  const fetchRecentAssets = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await listRecentPublicAssets(CANDIDATE_FETCH_LIMIT);

      if (error) {
        setAssets([]);
      } else {
        const validAssets = await filterAssetsWithValidPublicPreviews(data || [], DISPLAY_LIMIT);
        setAssets(validAssets);
      }
    } catch {
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentAssets();
  }, [fetchRecentAssets]);

  function getTypeIcon(type: string) {
    switch (type) {
      case 'clothing':
        return <Shirt className="w-3 h-3" />;
      case 'accessory':
        return <Sword className="w-3 h-3" />;
      case 'pet':
        return <Heart className="w-3 h-3" />;
      default:
        return <Sparkles className="w-3 h-3" />;
    }
  }

  function getTypeBadgeColor(type: string) {
    switch (type) {
      case 'clothing':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'accessory':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'pet':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  }

  if (loading) {
    return (
      <section className="bg-gradient-to-br from-gray-50 to-blue-50/30 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Community Creations</h2>
            <p className="text-lg text-gray-600">Recent generations from our creator community</p>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-gray-400">Loading community gallery...</div>
          </div>
        </div>
      </section>
    );
  }

  if (assets.length === 0) {
    return (
      <section className="bg-gradient-to-br from-gray-50 to-blue-50/30 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Community Creations</h2>
            <p className="text-lg text-gray-600">Recent generations from our creator community</p>
          </div>
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Community creations will appear here soon.</p>
            <p className="text-sm text-gray-400 mt-2">Be the first to create and list your assets!</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-to-br from-gray-50 to-blue-50/30 py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-3">Community Creations</h2>
          <p className="text-lg text-gray-600">Recent generations from our creator community</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {assets.map(({ asset, glbUrl, resolvedGlbUrl }) => {
            return (
              <div
                key={asset.id}
                onClick={() => navigate(`/assets/${asset.id}`)}
                className="bg-white rounded-xl border-2 border-gray-100 hover:border-orange-300 hover:shadow-xl transition-all p-4 cursor-pointer group"
              >
                <Mini3DViewer
                  glbUrl={glbUrl}
                  resolvedGlbUrl={resolvedGlbUrl}
                  assetId={asset.id}
                  width={250}
                  height={250}
                />

                <div className="mt-4">
                  <h3 className="font-bold text-gray-900 mb-2 truncate group-hover:text-orange-600 transition-colors">
                    {asset.name || 'Untitled Asset'}
                  </h3>

                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg border ${getTypeBadgeColor(
                        asset.generator_type
                      )}`}
                    >
                      {getTypeIcon(asset.generator_type)}
                      {asset.generator_type}
                    </span>
                    {asset.price_cents > 0 && (
                      <span className="text-sm font-bold text-gray-900">
                        {formatCurrency(asset.price_cents)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/marketplace')}
            className="px-8 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all font-semibold shadow-lg shadow-orange-600/20"
          >
            Browse Full Marketplace
          </button>
        </div>
      </div>
    </section>
  );
}
